import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

const LOOP_SELLING_PLAN_EVENT = 'loop:selling-plan:change';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from three different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 * 3. Loop Subscriptions selling plan change
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.currency = this.dataset.currency || 'USD';
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    document.addEventListener(LOOP_SELLING_PLAN_EVENT, this.handleSellingPlanChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    document.removeEventListener(LOOP_SELLING_PLAN_EVENT, this.handleSellingPlanChange);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  /**
   * Handles Loop Subscriptions selling plan change events.
   * @param {CustomEvent} event
   */
  handleSellingPlanChange = (event) => {
    const detail = event?.detail || {};
    const productId = detail.productId != null ? String(detail.productId) : null;
    if (!productId || productId !== this.dataset.productId) return;

    const priceMap = this.#readPriceMap(productId);
    if (!priceMap) return;

    const { price, compareAtPrice, isSale } = this.#resolvePriceForPlan(priceMap, detail);

    this.#renderPrice(price, compareAtPrice, isSale);
  };

  /**
   * Reads the embedded price map JSON for the given product.
   * @param {string} productId
   * @returns {object | null}
   */
  #readPriceMap(productId) {
    const root = this.closest('product-info, .shopify-section, dialog, body') || document;
    const script = /** @type {HTMLScriptElement|null} */ (
      root.querySelector?.(`script[data-price-map="${productId}"]`)
    );
    if (!script) return null;
    try {
      return JSON.parse(script.textContent || '{}');
    } catch (_) {
      return null;
    }
  }

  /**
   * Resolves the price values to display for the given selling plan.
   * @param {object} priceMap
   * @param {object} detail
   */
  #resolvePriceForPlan(priceMap, detail) {
    const base = priceMap.base || {};
    const allocations = Array.isArray(priceMap.allocations) ? priceMap.allocations : [];
    const sellingPlanId = detail.sellingPlanId != null ? String(detail.sellingPlanId) : null;

    if (detail.isOneTimePurchase || !sellingPlanId) {
      return {
        price: base.price,
        compareAtPrice: base.compare_at_price,
        isSale: Number(base.compare_at_price) > Number(base.price),
      };
    }

    const allocation = allocations.find((a) => String(a.id) === sellingPlanId) || null;
    if (!allocation) {
      return {
        price: base.price,
        compareAtPrice: base.compare_at_price,
        isSale: Number(base.compare_at_price) > Number(base.price),
      };
    }

    const price = allocation.per_delivery_price ?? allocation.price ?? base.price;
    const compareAtPrice = allocation.compare_at_price ?? base.compare_at_price;
    return {
      price,
      compareAtPrice,
      isSale: Number(compareAtPrice) > Number(price),
    };
  }

  /**
   * Renders the price block by toggling sale/regular pricing.
   * @param {number} price
   * @param {number} compareAtPrice
   * @param {boolean} isSale
   */
  #renderPrice(price, compareAtPrice, isSale) {
    const { priceContainer } = this.refs;
    if (!priceContainer) return;

    const { moneyFormat, currency } = this;
    const formattedPrice = formatMoney(price, moneyFormat, currency);
    const formattedCompare = formatMoney(compareAtPrice, moneyFormat, currency);

    const regular = priceContainer.querySelector('.price__regular');
    const sale = priceContainer.querySelector('.price__sale');
    const saleSpan = sale?.querySelector('.price-item--sale');
    const regularSpan = sale?.querySelector('.price-item--regular');
    const regularOnlySpan = regular?.querySelector('.price');

    if (isSale && sale && regular) {
      regular.classList.add('price__hidden');
      sale.classList.remove('price__hidden');
      if (saleSpan) saleSpan.textContent = formattedPrice;
      if (regularSpan) regularSpan.textContent = formattedCompare;
    } else if (regular) {
      regular.classList.remove('price__hidden');
      if (sale) sale.classList.add('price__hidden');
      if (regularOnlySpan) regularOnlySpan.textContent = formattedPrice;
    }
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
